import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight } from "lucide-react";

import { cn } from "../lib/utils";
import {
  MENU_CHECK,
  MENU_CONTENT,
  MENU_INDICATOR,
  MENU_INSET,
  MENU_ITEM,
  MENU_ITEM_DESTRUCTIVE,
  MENU_LABEL,
  MENU_SEPARATOR,
  MENU_SHORTCUT,
  OVERLAY_COLLISION_PADDING,
  OVERLAY_OFFSET,
} from "../lib/overlay";
import { Icon, type IconComponent } from "./icon";

/**
 * v2 DropdownMenu (redesign phase 5, #455). Storybook only until
 * implementation; see docs/design-library/04-implementation-notes.md. Same
 * export names as v1 `components/ui/dropdown-menu.tsx`, so the swap is a move.
 *
 * One menu for both apps: highlight is `--hover-overlay` (v1: `bg-muted`,
 * Neptune and ContextMenu: purple `bg-accent`), plus the inset focus ring for
 * keyboard users (see lib/overlay.ts). Items are Sora 14/20 Regular, 32px,
 * icons 16 (break #75).
 *
 * Checkbox and radio items show the same Check, left, in `--primary-accent`;
 * the text stays normal (break #76). `variant="destructive"` turns an item red
 * with a red tint on highlight (break #77); the confirm comes after. A
 * disabled item is neutral whatever its variant: 50% ink, never faded red.
 *
 * `disabled` + `disabledReason` (finding #21; break #177): the reason is a
 * caption line under the label and the item's description. Unlike a plain
 * disabled item (which Radix skips) it stays reachable by the arrow keys, so
 * screen readers hear why (`aria-disabled`, the WAI-ARIA menu pattern);
 * Enter does nothing and the menu stays open. Only icon and label fade; the
 * reason stays readable (`--ink-muted` on `--popover`).
 *
 * "Weitere Aktionen" (row and card menus) is always
 * `<IconButton icon={Ellipsis} label="Weitere Aktionen" />` as the trigger,
 * with `align="end"` on the content (break #81).
 */

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const MENU_FONT = "font-sans";

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent(
  { align = "start", sideOffset = OVERLAY_OFFSET, collisionPadding = OVERLAY_COLLISION_PADDING, className, style, ...props },
  ref,
) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(MENU_CONTENT, className)}
        style={{ maxHeight: "var(--radix-dropdown-menu-content-available-height)", ...style }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(function DropdownMenuSubContent(
  { sideOffset = 4, alignOffset = -5, collisionPadding = OVERLAY_COLLISION_PADDING, className, style, ...props },
  ref,
) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        ref={ref}
        sideOffset={sideOffset}
        // -(4px padding + 1px border): the sub menu's first item lines up with its trigger.
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        className={cn(MENU_CONTENT, className)}
        style={{ maxHeight: "var(--radix-dropdown-menu-content-available-height)", ...style }}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});

export type DropdownMenuItemVariant = "default" | "destructive";

export interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /** 16px, in the text colour. */
  icon?: IconComponent;
  variant?: DropdownMenuItemVariant;
  /** No icon, but text aligned with items that have one. */
  inset?: boolean;
  /** With `disabled`: why. A caption line under the label and the item's description. German UI text. */
  disabledReason?: string;
}

const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof DropdownMenuPrimitive.Item>, DropdownMenuItemProps>(
  function DropdownMenuItem(
    { icon, variant = "default", inset = false, disabled, disabledReason, className, children, onSelect, textValue, ...props },
    ref,
  ) {
    const reasonId = React.useId();
    const explained = !!disabled && !!disabledReason;
    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        // Explained: Radix keeps it in the roving focus; aria-disabled and onSelect make it inert.
        disabled={explained ? false : disabled}
        textValue={textValue ?? (explained && typeof children === "string" ? children : undefined)}
        onSelect={explained ? (event) => event.preventDefault() : onSelect}
        className={cn(
          MENU_ITEM,
          MENU_FONT,
          variant === "destructive" && !disabled && MENU_ITEM_DESTRUCTIVE,
          explained && "cursor-not-allowed items-start",
          inset && !icon && MENU_INSET,
          className,
        )}
        {...props}
        aria-disabled={explained ? true : props["aria-disabled"]}
        aria-describedby={explained ? [props["aria-describedby"], reasonId].filter(Boolean).join(" ") : props["aria-describedby"]}
      >
        {explained ? (
          <>
            {/* 16px icon centred on the label's 20px line. */}
            {icon && <Icon icon={icon} className="mt-0.5 opacity-50" />}
            <span className="flex min-w-0 flex-col">
              <span className="opacity-50">{children}</span>
              {/* aria-hidden keeps it out of the item's name; aria-describedby still reads it. Wraps at 224, so a long reason doesn't widen the menu. */}
              <span id={reasonId} aria-hidden="true" className="max-w-56 font-sans text-caption text-ink-muted">
                {disabledReason}
              </span>
            </span>
          </>
        ) : (
          <>
            {icon && <Icon icon={icon} />}
            {children}
          </>
        )}
      </DropdownMenuPrimitive.Item>
    );
  },
);

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem ref={ref} className={cn(MENU_ITEM, MENU_FONT, className)} {...props}>
      <span className={MENU_CHECK}>
        <DropdownMenuPrimitive.ItemIndicator className="flex">
          <Icon icon={Check} />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});

/** The current choice shows the same Check as a checkbox item; `role="menuitemradio"` carries the difference. */
const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.RadioItem ref={ref} className={cn(MENU_ITEM, MENU_FONT, className)} {...props}>
      <span className={MENU_CHECK}>
        <DropdownMenuPrimitive.ItemIndicator className="flex">
          <Icon icon={Check} />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});

export interface DropdownMenuSubTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> {
  icon?: IconComponent;
  inset?: boolean;
}

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  DropdownMenuSubTriggerProps
>(function DropdownMenuSubTrigger({ icon, inset = false, className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(MENU_ITEM, MENU_FONT, "aria-expanded:bg-hover-overlay", inset && !icon && MENU_INSET, className)}
      {...props}
    >
      {icon && <Icon icon={icon} />}
      {children}
      <span className={cn(MENU_INDICATOR, "ml-auto text-ink-muted")}>
        <Icon icon={ChevronRight} />
      </span>
    </DropdownMenuPrimitive.SubTrigger>
  );
});

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & { inset?: boolean }
>(function DropdownMenuLabel({ inset = false, className, ...props }, ref) {
  return <DropdownMenuPrimitive.Label ref={ref} className={cn(MENU_LABEL, inset && MENU_INSET, className)} {...props} />;
});

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return <DropdownMenuPrimitive.Separator ref={ref} className={cn(MENU_SEPARATOR, className)} {...props} />;
});

/** Keyboard shortcut at the right edge, e.g. "⌘D". Decorative: bind the key elsewhere. */
function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn(MENU_SHORTCUT, className)} {...props} />;
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
